import { useCallback, useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { toast } from 'sonner';
import api from '@/lib/api';
import useSchool, { BRAND_COLOR_FALLBACK } from '@/hooks/useSchool';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from '@/components/ui/select';
import { ArrowLeft, Wand2, Loader2 } from 'lucide-react';

function defaultDueIso() {
  const d = new Date();
  d.setDate(d.getDate() + 7);
  // YYYY-MM-DD — date-only input format (end-of-day time appended on submit)
  const tzo = d.getTimezoneOffset();
  const local = new Date(d.getTime() - tzo * 60000);
  return local.toISOString().slice(0, 10);
}

// Convert a YYYY-MM-DD value from a date input into a full ISO datetime
// at the end of that day in the user's local timezone (23:59:00 local).
function dateOnlyToEodIso(dateStr) {
  if (!dateStr) return null;
  const d = new Date(`${dateStr}T23:59:00`);
  if (isNaN(d.getTime())) return null;
  return d.toISOString();
}

export default function SchoolTeacherAssessmentNew() {
  const navigate = useNavigate();
  const [me, setMe] = useState(null);
  const school = useSchool(me?.school_id);
  const [assignments, setAssignments] = useState([]);
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);

  // Form state
  const [classId, setClassId] = useState('');
  const [subjectId, setSubjectId] = useState('');
  const [title, setTitle] = useState('');
  const [topic, setTopic] = useState('');
  const [numQs, setNumQs] = useState(10);
  const [releaseMode, setReleaseMode] = useState('teacher_approved');
  const [dueAt, setDueAt] = useState(defaultDueIso());
  const [duration, setDuration] = useState('');

  useEffect(() => {
    (async () => {
      try {
        const meRes = await api.get('/auth/me');
        setMe(meRes.data);
        const ts = await api.get('/teacher/me/assignments', { params: { status: 'active' } });
        setAssignments(ts.data || []);
      } catch (e) {
        toast.error(e?.response?.data?.detail || 'Failed to load context');
      } finally {
        setLoading(false);
      }
    })();
  }, []);

  // When teacher picks a class, default subject if exactly one assignment exists
  const classOptions = useMemo(() => {
    const seen = new Map();
    for (const a of assignments) {
      if (a?.school_class) seen.set(a.school_class.school_class_id, a.school_class);
    }
    return Array.from(seen.values());
  }, [assignments]);

  const subjectOptions = useMemo(() => {
    if (!classId) return [];
    return assignments
      .filter((a) => a.school_class_id === classId)
      .map((a) => a.subject)
      .filter(Boolean);
  }, [classId, assignments]);

  useEffect(() => {
    if (subjectOptions.length === 1) setSubjectId(subjectOptions[0].subject_id);
    else setSubjectId('');
  }, [subjectOptions]);

  const brandColor = school?.brand_color || BRAND_COLOR_FALLBACK;

  const handleGenerate = useCallback(async () => {
    if (!classId || !subjectId || !title.trim() || !topic.trim() || !dueAt) {
      toast.error('Please fill all required fields');
      return;
    }
    if (numQs < 1 || numQs > 25) {
      toast.error('Question count must be between 1 and 25');
      return;
    }
    const dueIso = dateOnlyToEodIso(dueAt);
    if (!dueIso || new Date(dueIso).getTime() <= Date.now()) {
      toast.error('Due date must be in the future');
      return;
    }
    setSubmitting(true);
    try {
      const body = {
        school_class_id: classId,
        subject_id: subjectId,
        title: title.trim(),
        topic_seed: topic.trim(),
        num_questions: Number(numQs),
        release_mode: releaseMode,
        due_at: dueIso,
      };
      if (duration) body.duration_minutes = Number(duration);
      const res = await api.post('/teacher/me/assessments', body);
      const aid = res.data?.assessment?.assessment_id;
      if (res.data?.generation_warning) {
        toast.warning(res.data.generation_warning);
      } else {
        toast.success('Draft created — review and approve each question.');
      }
      navigate(`/school-teacher/assessments/${aid}`);
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Generation failed');
    } finally {
      setSubmitting(false);
    }
  }, [classId, subjectId, title, topic, numQs, releaseMode, dueAt, duration, navigate]);

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-slate-50">
        <Loader2 className="animate-spin text-indigo-600" />
      </div>
    );
  }

  return (
    <div
      className="min-h-screen bg-gradient-to-br from-slate-50 via-white to-indigo-50"
      data-testid="school-teacher-assessment-new"
    >
      <header
        className="border-b bg-white/90 backdrop-blur sticky top-0 z-10"
        style={{ borderTopWidth: 4, borderTopColor: brandColor }}
      >
        <div className="max-w-3xl mx-auto px-6 py-4 flex items-center gap-3">
          <Button
            variant="ghost" size="sm"
            onClick={() => navigate('/school-teacher/assessments')}
            data-testid="back-to-list-btn"
          >
            <ArrowLeft size={16} className="mr-1.5" /> Back
          </Button>
          <h1 className="font-bold text-slate-900">New assessment</h1>
        </div>
      </header>

      <main className="max-w-3xl mx-auto px-6 py-8">
        <div className="bg-white border rounded-xl p-6 shadow-sm space-y-5">
          <div className="space-y-2">
            <Label>Class *</Label>
            <Select value={classId} onValueChange={setClassId}>
              <SelectTrigger data-testid="form-class-select">
                <SelectValue placeholder="Select a class" />
              </SelectTrigger>
              <SelectContent>
                {classOptions.map((c) => (
                  <SelectItem key={c.school_class_id} value={c.school_class_id}>
                    {c.name} (Grade {c.grade}{c.section ? `-${c.section}` : ''})
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          <div className="space-y-2">
            <Label>Subject *</Label>
            <Select value={subjectId} onValueChange={setSubjectId} disabled={!classId}>
              <SelectTrigger data-testid="form-subject-select">
                <SelectValue placeholder={classId ? 'Select a subject' : 'Pick a class first'} />
              </SelectTrigger>
              <SelectContent>
                {subjectOptions.map((s) => (
                  <SelectItem key={s.subject_id} value={s.subject_id}>{s.name}</SelectItem>
                ))}
              </SelectContent>
            </Select>
            <p className="text-xs text-slate-500">
              You can only choose subjects you teach for the selected class.
            </p>
          </div>

          <div className="space-y-2">
            <Label htmlFor="title">Title *</Label>
            <Input
              id="title"
              value={title}
              onChange={(e) => setTitle(e.target.value)}
              placeholder="e.g., Polynomials – Quick check"
              maxLength={140}
              data-testid="form-title-input"
            />
          </div>

          <div className="space-y-2">
            <Label htmlFor="topic">Topic seed *</Label>
            <Input
              id="topic"
              value={topic}
              onChange={(e) => setTopic(e.target.value)}
              placeholder="e.g., Quadratic equations and their roots"
              maxLength={200}
              data-testid="form-topic-input"
            />
            <p className="text-xs text-slate-500">
              We'll search the curriculum for this grade and ground every question in the retrieved chunks.
            </p>
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <div className="space-y-2">
              <Label htmlFor="numq">Questions (1–25) *</Label>
              <Input
                id="numq" type="number" min="1" max="25"
                value={numQs}
                onChange={(e) => setNumQs(e.target.value)}
                data-testid="form-numq-input"
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="dur">Time limit (minutes, optional)</Label>
              <Input
                id="dur" type="number" min="1" max="240"
                value={duration}
                onChange={(e) => setDuration(e.target.value)}
                placeholder="No limit"
                data-testid="form-duration-input"
              />
            </div>
          </div>

          <div className="space-y-2">
            <Label>Release mode *</Label>
            <Select value={releaseMode} onValueChange={setReleaseMode}>
              <SelectTrigger data-testid="form-release-mode">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="auto">Auto-release on submit (results visible immediately)</SelectItem>
                <SelectItem value="teacher_approved">Hold-back — release after my feedback</SelectItem>
              </SelectContent>
            </Select>
          </div>

          <div className="space-y-2">
            <Label htmlFor="due">Due date *</Label>
            <Input
              id="due" type="date"
              value={dueAt}
              onChange={(e) => setDueAt(e.target.value)}
              data-testid="form-due-input"
            />
            <p className="text-xs text-slate-500">
              Students cannot start or submit after end of this day.
            </p>
          </div>

          <div className="pt-3 border-t">
            <Button
              onClick={handleGenerate}
              disabled={submitting}
              className="w-full text-white"
              style={{ backgroundColor: brandColor }}
              data-testid="generate-btn"
            >
              {submitting ? (
                <><Loader2 size={16} className="animate-spin mr-2" /> Generating MCQs…</>
              ) : (
                <><Wand2 size={16} className="mr-2" /> Generate questions</>
              )}
            </Button>
            <p className="text-xs text-slate-500 mt-2 text-center">
              You'll review and approve each question before publishing to your class.
            </p>
          </div>
        </div>
      </main>
    </div>
  );
}
